// DEV-351 follow-up: employee/dependent API responses carry a masked SSN
// (***-**-1234). Flows that need the real value fetch it on demand from the
// audited GET /api/employees/:id/ssn.
import { isMaskedSsn } from "@shared/utils/ssnRedaction";

export { isMaskedSsn };

export type RevealedSsns = {
  employeeId: number;
  ssn: string | null;
  dependents: Array<{ id: number; ssn: string | null }>;
};

export async function fetchFullSsns(employeeId: number, reason: string): Promise<RevealedSsns> {
  const res = await fetch(
    `/api/employees/${employeeId}/ssn?reason=${encodeURIComponent(reason)}`,
    { credentials: "include" },
  );
  if (!res.ok) throw new Error("Could not load SSN");
  return res.json();
}

/** The revealed SSN of the employee, or of one of their dependents when `dependentId` is given. */
export function pickRevealedSsn(revealed: RevealedSsns, dependentId?: number): string | null {
  if (dependentId == null) return revealed.ssn ?? null;
  return revealed.dependents.find((d) => Number(d.id) === Number(dependentId))?.ssn ?? null;
}

/** Replace masked `ssn` on an employee row and its dependent rows with the revealed values. */
export function applyFullSsns<E extends { ssn?: string | null }, D extends { id: number; ssn?: string | null }>(
  employee: E,
  dependents: D[],
  revealed: RevealedSsns,
): { employee: E; dependents: D[] } {
  const depSsn = new Map(revealed.dependents.map((d) => [Number(d.id), d.ssn]));
  return {
    employee: { ...employee, ssn: revealed.ssn },
    dependents: dependents.map((d) =>
      depSsn.has(Number(d.id)) ? { ...d, ssn: depSsn.get(Number(d.id)) ?? null } : d,
    ),
  };
}

/**
 * SSN value for a save payload. `undefined` = unchanged (still masked, or equal
 * to the revealed original) — omit it so the stored SSN is left alone.
 */
export function ssnToSave(
  formValue: string | null | undefined,
  revealedOriginal: string | null | undefined,
): string | null | undefined {
  if (isMaskedSsn(formValue)) return undefined;
  const digits = (v: string | null | undefined) => String(v ?? "").replace(/\D/g, "");
  if (revealedOriginal != null && digits(formValue) === digits(revealedOriginal)) return undefined;
  return formValue;
}
